import React, { useEffect, useState } from "react";
import { getSupabase, supabasePublic, supabaseSchema } from "../supabaseClient";
import { beginActivity, endActivity } from "../../utils/activityTracker";
import {
  Button,
  Text,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  bucketName_files,
  data_column,
  tableName,
  allowedFileTypes,
  getConcatenatedInput,
  file_reference_column,
  colid,
  colid2,
  BASE_URLS,
  prompts,
} from "../env.js";
import { mergeUploadFiles, filterAllowed, formatBytes } from "./uploaderFiles";
import ReportCameraButton from "../reports/ReportCameraButton";
import {
  continueOcrExtractionInBatches,
  continueOcrIncompleteToast,
  continueOcrProgressLabel,
  continueOcrUpdatedTruncation,
} from "../../utils/continueOcrExtraction";

const storedConcatenatedInput = getConcatenatedInput();

// Helper function to get user's schema - uses single source of truth from supabaseClient
const getUserSchema = async () => {
  return supabaseSchema || "public";
};

// Sanitize filename only (preserves extension, removes invalid characters for Supabase storage)
const sanitizeFilename = (filename) => {
  if (!filename) return "file.png";

  // Split filename and extension
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) {
    // No extension, sanitize entire name
    return (
      filename.replace(/[^a-zA-Z0-9._-]/g, "_").replace(/_+/g, "_") || "file"
    );
  }

  const nameWithoutExt = filename.substring(0, lastDotIndex);
  const extension = filename.substring(lastDotIndex + 1).toLowerCase();

  // Sanitize the name part - replace invalid chars with underscore
  // Invalid chars for Supabase: spaces, colons, commas, @, etc.
  const sanitizedName =
    nameWithoutExt
      .replace(/[^a-zA-Z0-9._-]/g, "_") // Replace invalid chars with underscore
      .replace(/_+/g, "_") // Replace multiple underscores with single
      .replace(/^_+|_+$/g, "") || "file"; // Remove leading/trailing underscores

  return `${sanitizedName}.${extension}`;
};

const Uploader = ({
  selection,
  table,
  onProcessingChange,
  /** When set (e.g. Vita-Med Drive folder path), uploads go here — no schedule row or classification. */
  driveFolderPath,
  onDriveUploadComplete,
  /** Fired after the schedule-flow upload loop finishes, so callers (e.g. the
   *  clinic Reports panel) can refresh their file list. */
  onUploadComplete,
  /** Optional render-prop for the trigger button, letting a caller supply its
   *  own styled button while reusing all the upload logic + modal below.
   *  Receives { onClick, disabled }. Falls back to the default Chakra button. */
  renderTrigger,
  /** Quiet mode (e.g. the clinic Reports panel): dismiss the modal as soon as
   *  the upload kicks off and suppress the per-file success alerts, so OCR /
   *  classification runs in the background instead of trapping the user behind
   *  "OK" dialogs. Progress is surfaced via onProcessingChange; the list
   *  refreshes via onUploadComplete. */
  quiet = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [files, setFiles] = useState([]);
  // Drag-and-drop highlight + a note when dropped files were skipped for being
  // an unsupported type (drop bypasses the file input's `accept` filter).
  const [isDragging, setIsDragging] = useState(false);
  const [rejectedNote, setRejectedNote] = useState("");
  const toast = useToast();

  // Truncation modal state
  const {
    isOpen: isTruncationModalOpen,
    onOpen: onTruncationModalOpen,
    onClose: onTruncationModalClose,
  } = useDisclosure();
  const [truncationInfo, setTruncationInfo] = useState(null);
  const [isContinuingExtraction, setIsContinuingExtraction] = useState(false);
  const [continueProgress, setContinueProgress] = useState(null);

  // View extracted text state
  const {
    isOpen: isViewTextModalOpen,
    onOpen: onViewTextModalOpen,
    onClose: onViewTextModalClose,
  } = useDisclosure();
  const [viewingTextData, setViewingTextData] = useState(null);

  // Helper functions for localStorage truncation tracking
  const TRUNCATION_STORAGE_KEY = 'pending_truncations';
  const DECLINED_TRUNCATION_KEY = 'declined_truncations';

  const savePendingTruncation = (filePath, truncationData) => {
    try {
      const stored = localStorage.getItem(TRUNCATION_STORAGE_KEY);
      const pending = stored ? JSON.parse(stored) : {};
      pending[filePath] = truncationData;
      localStorage.setItem(TRUNCATION_STORAGE_KEY, JSON.stringify(pending));
      console.log('Saved pending truncation to localStorage:', filePath, truncationData);
    } catch (e) {
      console.error('Error saving truncation storage:', e);
    }
  };

  const saveDeclinedTruncation = (filePath) => {
    try {
      const stored = localStorage.getItem(DECLINED_TRUNCATION_KEY);
      const declined = stored ? JSON.parse(stored) : {};
      declined[filePath] = true;
      localStorage.setItem(DECLINED_TRUNCATION_KEY, JSON.stringify(declined));
      console.log('Saved declined truncation to localStorage:', filePath);
    } catch (e) {
      console.error('Error saving declined truncation storage:', e);
    }
  };

  const clearPendingTruncation = (filePath) => {
    try {
      const stored = localStorage.getItem(TRUNCATION_STORAGE_KEY);
      const pending = stored ? JSON.parse(stored) : {};
      delete pending[filePath];
      localStorage.setItem(TRUNCATION_STORAGE_KEY, JSON.stringify(pending));
      console.log('Cleared pending truncation from localStorage:', filePath);
    } catch (e) {
      console.error('Error clearing truncation storage:', e);
    }
  };

  // Handle continuing extraction for truncated documents
  const handleContinueExtraction = async () => {
    if (!truncationInfo) return;

    setIsContinuingExtraction(true);
    setContinueProgress(null);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // The server OCRs a batch of pages per request; call until none remain.
      const data = await continueOcrExtractionInBatches(async () => {
        const response = await fetch(`${baseUrl}/api/fax/continue-ocr-extraction/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_path: truncationInfo.file_path }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to continue extraction");
        }

        return response.json();
      }, { onProgress: setContinueProgress });

      // Pages left (the OCR service stopped responding mid-way): keep the
      // pending entry so the prompt comes back, and say what was saved.
      const incomplete = continueOcrIncompleteToast(data);
      if (incomplete) {
        // Keep the entry, with the counts that are left now.
        savePendingTruncation(
          truncationInfo.file_path,
          continueOcrUpdatedTruncation(truncationInfo, data),
        );
        toast({ ...incomplete, duration: 8000, isClosable: true });
      } else {
        // Clear from localStorage since user made a choice
        clearPendingTruncation(truncationInfo.file_path);

        toast({
          title: "Success",
          description: data.message || `Successfully processed remaining ${truncationInfo.remaining_pages} pages`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      onTruncationModalClose();
      setTruncationInfo(null);
    } catch (error) {
      console.error("Error continuing extraction:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to continue extraction",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsContinuingExtraction(false);
      setContinueProgress(null);
    }
  };

  // Handle viewing extracted text
  const handleViewExtractedText = async () => {
    if (!truncationInfo) return;

    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(`${baseUrl}/api/fax/get-extracted-text/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ file_path: truncationInfo.file_path }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch extracted text");
      }

      const data = await response.json();
      setViewingTextData({
        filename: truncationInfo.filename,
        extractedText: data.extracted_text || "No text extracted yet.",
        pagesProcessed: truncationInfo.pages_processed,
        totalPages: truncationInfo.total_pages,
      });
      onViewTextModalOpen();
    } catch (error) {
      console.error("Error fetching extracted text:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch extracted text",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle declining to continue extraction
  const handleDeclineContinueExtraction = () => {
    if (truncationInfo) {
      // Clear from pending and mark as declined so we don't show the modal again
      clearPendingTruncation(truncationInfo.file_path);
      saveDeclinedTruncation(truncationInfo.file_path);
    }
    onTruncationModalClose();
    setTruncationInfo(null);
    toast({
      title: "Extraction Complete",
      description: "Document was processed with the first 10 pages only.",
      status: "info",
      duration: 5000,
      isClosable: true,
    });
  };

  const disabled = () => {
    if (driveFolderPath !== undefined && driveFolderPath !== null) {
      return !String(driveFolderPath).trim();
    }
    return !selection || Object.keys(selection).length === 0;
  };

  // Add files from either the browse dialog or a drag-and-drop, merging into
  // the current queue and dropping any unsupported types (drag-and-drop
  // bypasses the file input's `accept`, so we enforce the allow-list here).
  const addFiles = (incoming) => {
    const { accepted, rejected } = filterAllowed(incoming, allowedFileTypes);
    setFiles((prev) => mergeUploadFiles(prev, accepted));
    setRejectedNote(
      rejected.length ? `${rejected.length} file(s) skipped — unsupported type.` : ""
    );
  };

  const handleFileChange = (event) => {
    addFiles(event.target.files);
    // Clear the input value so re-selecting the same file still fires onChange.
    event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer?.files);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFileToStorage = async (
    filePath,
    file,
    bucketName,
    { silent = false } = {}
  ) => {
    const supabase = await getSupabase();

    // Determine content type based on file extension (minimal - only 3 image types)
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const contentType =
      fileExtension === "png"
        ? "image/png"
        : fileExtension === "jpg" || fileExtension === "jpeg"
        ? "image/jpeg"
        : fileExtension === "pdf"
        ? "application/pdf"
        : fileExtension === "csv"
        ? "text/csv"
        : fileExtension === "txt"
        ? "text/plain"
        : fileExtension === "html"
        ? "text/html"
        : fileExtension === "json"
        ? "application/json"
        : file.type || "application/octet-stream";

    const { error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        contentType: contentType,
      });

    if (uploadError) {
      console.error("Error uploading file:", uploadError);
      alert(
        `Error uploading file to storage: ${uploadError.message || uploadError}`
      );
      throw uploadError;
    }
    if (!silent) {
      alert(`File uploaded successfully to ${filePath}!`);
    }
  };

  const updateSupabaseTable = async (
    updateObject,
    selectedRowId,
    { silent = false } = {}
  ) => {
    const supabase = await getSupabase();
    const { data: updateData, error: updateError } = await supabase
      .from(tableName)
      .update(updateObject)
      .eq("id", selection.id);

    if (updateError) {
      console.error("Error updating rows in Supabase:", updateError);
      if (!silent) alert("Error updating rows in Supabase.");
      throw updateError;
    }

    console.log("Rows updated successfully:", updateData);
    if (!silent) alert("Rows updated successfully!");
  };

  const classifyUploadedFile = async (filePath) => {
    try {
      const hostname = window.location.hostname;
      const backendUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      console.log(
        `🔍 Calling classification endpoint: ${backendUrl}/api/ai-assistant/classify-file/`
      );
      console.log(`🔍 File path: ${filePath}`);

      // Get user ID from session for role-specific extraction
      let userId = null;
      try {
        const supabase = await getSupabase();
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.user) {
          userId = sessionData.session.user.id;
          console.log(`🔍 Got user ID for classification: ${userId}`);
        }
      } catch (e) {
        console.warn("Failed to get user session:", e);
      }

      // Get selected models from localStorage to use during classification (OCR)
      let modelIds = null;
      try {
        const savedModels = localStorage.getItem("aiAssistant_selectedModels");
        if (savedModels) {
          modelIds = JSON.parse(savedModels);
        }
      } catch (e) {
        console.warn("Failed to read selectedModels from localStorage:", e);
      }

      const response = await fetch(
        `${backendUrl}/api/ai-assistant/classify-file/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            file_path: filePath,
            userId: userId,
            modelIds: modelIds,
            modelId: modelIds && modelIds.length > 0 ? modelIds[0] : null,
          }),
        }
      );

      console.log(
        `🔍 Classification response status: ${response.status} ${response.statusText}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ Classification failed with status ${response.status}:`,
          errorText
        );
        let errorMessage = `Classification failed: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      // classify-file returns 200 even when the cache write itself failed,
      // reporting it only in cache_error. Left unread, a failed write (e.g. a
      // missing column -> PGRST204) looks like a clean upload while the file
      // silently ends up with no classification, summary, metadata, or badges.
      if (data && data.cache_stored === false) {
        throw new Error(
          data.cache_error ||
            "The file uploaded but its classification could not be saved."
        );
      }
      console.log(
        `✅ File ${filePath} classified as: ${data.classification} (cached: ${data.cached})`
      );
      return data;
    } catch (error) {
      console.error(`❌ Error classifying file ${filePath}:`, error);
      throw new Error(`Classification error: ${error.message}`);
    }
  };

  const handleUploadInner = async () => {
    if (!files.length) {
      alert("Please select files to upload.");
      return;
    }

    if (driveFolderPath !== undefined && driveFolderPath !== null) {
      const base = String(driveFolderPath).replace(/\/$/, "");
      if (!base) {
        alert("Open a Drive folder first.");
        return;
      }
      try {
        for (const file of files) {
          const sanitizedFileName = sanitizeFilename(file.name);
          const filePath = `${base}/${sanitizedFileName}`;
          if (onProcessingChange) {
            onProcessingChange(filePath, {
              status: "uploading",
              message: "Uploading file...",
              scheduleId: null,
              folderPath: base,
            });
          }
          await uploadFileToStorage(filePath, file, bucketName_files, {
            silent: true,
          });
          if (onProcessingChange) {
            onProcessingChange(filePath, null);
          }
        }
        alert(`Uploaded ${files.length} file(s) to Drive.`);
        if (typeof onDriveUploadComplete === "function") {
          onDriveUploadComplete();
        }
      } catch (error) {
        console.error("Error during file upload:", error);
        alert("An error occurred while uploading the files.");
      }
      setFiles([]);
      setRejectedNote("");
      onClose();
      return;
    }

    try {
      const selectedRow = selection;
      if (!selectedRow || !selectedRow.id) {
        alert("Please select a patient row.");
        return;
      }

      // Use EHR_ID from env.js (prompts) instead of email (colid) for folder name
      // Get EHR_ID field name from env.js prompts
      const ehrIdFieldName =
        prompts.find((p) => p[0] === "EHR_ID")?.[0] || "EHR_ID";
      let ehrId =
        selectedRow[ehrIdFieldName] ||
        selectedRow.EHR_ID ||
        selectedRow.ehr_id ||
        "";

      // Ensure we're not accidentally using email - explicitly check it's not an email
      // This matches the backend logic in ai_assistant.py
      if (ehrId && typeof ehrId === "string" && ehrId.includes("@")) {
        console.warn(
          `EHR_ID field contains email (${ehrId}) for patient. Skipping email, using underscore suffix instead.`
        );
        ehrId = "";
      }

      // Format: patientname_EHR_ID (from env.js)
      const folderName = ehrId
        ? `${selectedRow[colid2]}_${ehrId}`
        : `${selectedRow[colid2]}_`;
      const dateFolder = selectedRow.date_
        ? selectedRow.date_.split("-").slice(1).join("-") +
          "-" +
          selectedRow.date_.slice(2, 4)
        : "";

      // Get user's schema for file path
      const userSchema = await getUserSchema();
      const schemaPrefix = userSchema && userSchema !== 'public' ? `${userSchema}/` : '';
      console.log(`📁 File upload - User schema: ${userSchema}, Schema prefix: '${schemaPrefix}'`);

      // Quiet mode: patient + schema are resolved, so dismiss the modal now and
      // let the upload + OCR run in the background. `filesToUpload` captures the
      // queue so clearing the modal state below doesn't cut the loop short.
      const filesToUpload = files;
      if (quiet) {
        setFiles([]);
        setRejectedNote("");
        onClose();
      }

      for (const file of filesToUpload) {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        const baseFolder =
          file.type === "application/json" ? "New/JSON" : "New/Reports";
        // Sanitize only the filename (folder path stays as is)
        const sanitizedFileName = sanitizeFilename(file.name);
        // Include schema in path for non-public schemas: New/Reports/{schema}/PatientName_EHR/date/file
        const filePath = `${baseFolder}/${schemaPrefix}${folderName}/${dateFolder}/${sanitizedFileName}`;

        // Notify that upload is starting
        if (onProcessingChange) {
          onProcessingChange(filePath, {
            status: 'uploading',
            message: 'Uploading file...',
            scheduleId: selectedRow.id,
            folderPath: `${baseFolder}/${schemaPrefix}${folderName}/${dateFolder}`
          });
        }

        await uploadFileToStorage(filePath, file, bucketName_files, {
          silent: quiet,
        });

        const columnToUpdate =
          file.type === "application/json"
            ? data_column
            : file_reference_column;
        // Include schema in the folder path stored in database
        const folderToUse = `${baseFolder}/${schemaPrefix}${folderName}/${dateFolder}`;
        const updateObject = { [columnToUpdate]: folderToUse };

        await updateSupabaseTable(updateObject, selectedRow.id, {
          silent: quiet,
        });

        // Automatically classify report files (not JSON files)
        if (fileExtension !== "json") {
          try {
            // Notify that classification/extraction is starting
            if (onProcessingChange) {
              onProcessingChange(filePath, {
                status: 'extracting',
                message: 'Extracting text and metadata...',
                scheduleId: selectedRow.id,
                folderPath: `${baseFolder}/${schemaPrefix}${folderName}/${dateFolder}`
              });
            }
            
            console.log(
              `🔍 Starting classification for file: ${file.name} at path: ${filePath}`
            );
            const classificationResult = await classifyUploadedFile(filePath);
            console.log(
              `✅ Successfully classified ${file.name}:`,
              classificationResult
            );

            // Check if document was truncated
            if (classificationResult.was_truncated && classificationResult.truncation_info) {
              console.log('📄 Document was truncated during upload:', classificationResult.truncation_info);
              const truncationData = {
                file_path: classificationResult.file_path || filePath,
                filename: classificationResult.filename || file.name,
                total_pages: classificationResult.truncation_info.total_pages,
                pages_processed: classificationResult.truncation_info.pages_processed,
                remaining_pages: classificationResult.truncation_info.remaining_pages,
              };

              // Save to localStorage so it persists
              savePendingTruncation(truncationData.file_path, truncationData);

              // Show truncation modal
              setTruncationInfo(truncationData);
              onTruncationModalOpen();
            }

            // Classification successful - clear processing state
            if (onProcessingChange) {
              onProcessingChange(filePath, null);
            }
          } catch (classifyError) {
            // Log detailed error but don't fail the upload
            console.error(
              `❌ Failed to classify file ${file.name}:`,
              classifyError
            );
            console.error(`Error details:`, {
              message: classifyError.message,
              stack: classifyError.stack,
              filePath: filePath,
            });
            // Don't show alert for classification failure - upload was successful
            console.warn(
              `⚠️ Classification failed for ${file.name}, but file uploaded successfully`
            );
            // Clear processing state even on error
            if (onProcessingChange) {
              onProcessingChange(filePath, null);
            }
          }
        } else {
          // For JSON files, clear processing state immediately after upload
          if (onProcessingChange) {
            onProcessingChange(filePath, null);
          }
        }
      }
    } catch (error) {
      console.error("Error during file upload:", error);
      if (quiet) {
        toast({
          title: "Upload failed",
          description: "An error occurred while uploading the files.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        alert("An error occurred while uploading the files.");
      }
    }

    // In quiet mode the modal was already dismissed before processing began.
    if (!quiet) {
      setFiles([]);
      setRejectedNote("");
      onClose();
    }
    // Let callers (e.g. the Reports panel) refresh now that files have landed.
    if (onUploadComplete) onUploadComplete();
  };

  const handleUpload = async () => {
    beginActivity("uploading");
    try {
      await handleUploadInner();
    } finally {
      endActivity("uploading");
    }
  };

  const handleClose = () => {
    setFiles([]);
    setRejectedNote("");
    setIsDragging(false);
    onClose();
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onClick: onOpen, disabled: disabled() })
      ) : (
        <Tooltip
          label={
            driveFolderPath !== undefined && driveFolderPath !== null
              ? !String(driveFolderPath).trim()
                ? "Open or navigate to a folder first."
                : "Upload files (same types as Schedules) to this folder."
              : "You need to make a selection before uploading."
          }
          isDisabled={!disabled()}
          placement="top"
        >
          <Button onClick={onOpen} isDisabled={disabled()} colorScheme="blue" size="sm">
            Upload
          </Button>
        </Tooltip>
      )}

      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload Files</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Drop zone doubles as a click-to-browse control: the hidden file
                input lives inside the <label>, so a click anywhere opens the
                dialog while drag-and-drop is handled on the same element. */}
            <Box
              as="label"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              cursor="pointer"
              borderWidth="2px"
              borderStyle="dashed"
              borderRadius="md"
              px={4}
              py={8}
              transition="all 0.15s"
              borderColor={isDragging ? "blue.400" : "gray.300"}
              bg={isDragging ? "blue.50" : "gray.50"}
              _hover={{ borderColor: "blue.400", bg: "blue.50" }}
            >
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept={allowedFileTypes.map((type) => `.${type}`).join(",")}
                style={{ display: "none" }}
              />
              <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                Drag &amp; drop files here
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                or click to browse
              </Text>
              <Text fontSize="xs" color="gray.400" mt={1}>
                {allowedFileTypes.map((type) => type.toUpperCase()).join(", ")}
              </Text>
            </Box>

            {/* Take a photo of a paper document (iPad rear camera) straight into
                the same queue as the picker and drag-drop — addFiles is the one
                intake point, so the file goes through filterAllowed and the
                existing upload + classify path unchanged. */}
            <Flex justify="center" mt={3}>
              <ReportCameraButton onCapture={(file) => addFiles([file])} />
            </Flex>

            {rejectedNote && (
              <Text fontSize="xs" color="orange.500" mt={2}>
                {rejectedNote}
              </Text>
            )}

            {files.length > 0 && (
              <VStack align="stretch" spacing={1.5} mt={3} maxH="220px" overflowY="auto">
                {files.map((file, index) => (
                  <Flex
                    key={`${file.name}-${index}`}
                    align="center"
                    gap={2}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={2}
                    py={1.5}
                  >
                    <Text fontSize="sm" flex="1" noOfLines={1} title={file.name}>
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500" flexShrink={0}>
                      {formatBytes(file.size)}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      flexShrink={0}
                      onClick={() => removeFile(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      ✕
                    </Button>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Text fontSize="xs" color="gray.500" mr="auto">
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </Text>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleUpload}
              isDisabled={!files.length}
            >
              Upload
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Truncation Confirmation Modal */}
      <Modal isOpen={isTruncationModalOpen} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false}>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxW="600px">
          <ModalHeader fontSize="lg">Document Truncated</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm">
                The document <strong>{truncationInfo?.filename}</strong> has{" "}
                <strong>{truncationInfo?.total_pages} pages</strong>, but only the first{" "}
                <strong>{truncationInfo?.pages_processed} pages</strong> were processed for AI extraction.
              </Text>
              <Text fontSize="sm">
                There are <strong>{truncationInfo?.remaining_pages} remaining pages</strong> that have not been processed.
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                Would you like to process the remaining pages and append them to the extracted content?
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" size="sm" fontSize="xs" mr={3} onClick={handleViewExtractedText}>
              View Extracted Text
            </Button>
            <Button variant="ghost" size="sm" fontSize="xs" mr={3} onClick={handleDeclineContinueExtraction}>
              No, this is fine
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              fontSize="xs"
              onClick={handleContinueExtraction}
              isLoading={isContinuingExtraction}
              loadingText={continueOcrProgressLabel(continueProgress)}
            >
              Yes, process remaining pages
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Extracted Text Modal */}
      <Modal isOpen={isViewTextModalOpen} onClose={onViewTextModalClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent maxW="700px" maxH="80vh">
          <ModalHeader>
            Extracted Text - {viewingTextData?.filename}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Pages processed: {viewingTextData?.pagesProcessed} of {viewingTextData?.totalPages}
              </Text>
              <Text
                as="pre"
                whiteSpace="pre-wrap"
                fontFamily="mono"
                fontSize="sm"
                bg="gray.50"
                p={4}
                borderRadius="md"
                maxH="50vh"
                overflowY="auto"
              >
                {viewingTextData?.extractedText}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewTextModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Uploader;
